import * as React from 'react';
import * as _ from 'lodash';
import { UilUser, UilCrosshairs } from '@iconscout/react-unicons';

import {
    Audience,
    AudienceRuleOption,
    Panel,
    PanelType,
    Project,
    Question,
    QuestionSummary
} from '@nexxt/common/types';
import AudienceRule from './AudienceRule';
import { AUDIENCE_RULE_TYPES } from '../../../interface/project.interface';
import AudienceSetting from './AudienceSetting';
import AudienceSummary from './AudienceSummary';
import ValidationService from '@nexxt/common/services/ValidationService';
import PanelSourceSettings from './PanelSourceSettings';
import { dataTestid } from '../../../constants/dataTestid';

interface UserDefinedAudienceGroupProps {
    questionList: any[];
    currentKeyName: AUDIENCE_RULE_TYPES;
    isAdd: boolean;
    isEdit: boolean;
    isEdited: boolean;
    isSaving: boolean;
    addAudienceRule: any;
    audience: Audience;
    previousAudience: Audience;
    showError: boolean;
    currentProject: Project;
    projectHealth: any;
    validationService: ValidationService;
    audienceRuleIndices: any;
    isIncaPanelEnabled: boolean;
    disableByFieldName: (keyName?: string) => boolean;
    onAddDynamicQuestion: (
        selectedQuestion: any,
        audienceRuleOptions: AudienceRuleOption[],
        keyname: AUDIENCE_RULE_TYPES
    ) => void;
    onUpdateDynamicQuestion: (
        audienceRuleOptions: AudienceRuleOption[],
        keyname: AUDIENCE_RULE_TYPES,
        index: number
    ) => void;
    getQuestionList: (keyName) => QuestionSummary[] | Question[];
    handleProjectSettings: (keyName: string, value: any) => void;
    confirmPanelSwitching: (panelType: {
        value: PanelType;
        label: string;
    }) => void;
    onCancelAudienceRule: (index, keyName) => void;
    onDeleteAudienceRule: (index, keyName) => void;
    onEditAudienceRule: (index, keyName) => void;
    onUpdateAudience: (index, rule, keyName, validateRule) => void;
    onSaveAudienceRule: (index, keyName) => void;
    onSaveAudience: () => void;
    onCountryChange: (countries: { value: string; label: string }[]) => void;
    openCustomScreenerModal: (screenerType: AUDIENCE_RULE_TYPES) => void;
    updatePanelSettings: (index: number, panel: Panel) => void;
}

interface UserDefinedAudienceGroupState {}

class UserDefinedAudienceGroup extends React.Component<
    UserDefinedAudienceGroupProps,
    UserDefinedAudienceGroupState
> {
    renderAudienceRuleList = (
        keyName = AUDIENCE_RULE_TYPES.CUSTOM_RULE
    ): JSX.Element[] => {
        const {
            audience,
            projectHealth,
            validationService,
            questionList,
            getQuestionList,
            onDeleteAudienceRule,
            onCancelAudienceRule,
            onEditAudienceRule,
            onUpdateAudience,
            onSaveAudienceRule,
            onUpdateDynamicQuestion,
            disableByFieldName
        } = this.props;
        const filteredQuestionList = getQuestionList(keyName);
        return audience[keyName as string].map((audienceRule, index) => {
            return (
                <AudienceRule
                    key={index}
                    index={index}
                    keyName={keyName}
                    audienceRule={audienceRule}
                    filteredQuestionList={filteredQuestionList}
                    questionList={questionList}
                    currentProject={this.props.currentProject}
                    health={
                        projectHealth[audienceRule.selectedQuestion.questionId]
                    }
                    totalSampleSize={audience.totalSampleSize}
                    onEdit={(index) => onEditAudienceRule(index, keyName)}
                    onCancel={(index) => onCancelAudienceRule(index, keyName)}
                    onSave={(index) => onSaveAudienceRule(index, keyName)}
                    onDelete={(index) => onDeleteAudienceRule(index, keyName)}
                    onRuleUpdate={(index, rule, validateRule) =>
                        onUpdateAudience(index, rule, keyName, validateRule)
                    }
                    onUpdateDynamicQuestion={onUpdateDynamicQuestion}
                    validationService={validationService}
                    disableByFieldName={disableByFieldName}
                />
            );
        });
    };

    renderAddAudienceRule = (
        keyName = AUDIENCE_RULE_TYPES.CUSTOM_RULE
    ): JSX.Element => {
        const {
            isAdd,
            currentKeyName,
            addAudienceRule,
            audience,
            validationService,
            questionList,
            getQuestionList,
            onDeleteAudienceRule,
            onCancelAudienceRule,
            onEditAudienceRule,
            onUpdateAudience,
            onSaveAudienceRule,
            onAddDynamicQuestion,
            disableByFieldName
        } = this.props;
        const filteredQuestionList = getQuestionList(keyName);
        if (isAdd && keyName === currentKeyName) {
            return (
                <AudienceRule
                    key={'NEW_AUDIENCE_RULE'}
                    index={-1}
                    keyName={keyName}
                    onDelete={(index) => onDeleteAudienceRule(index, keyName)}
                    onCancel={(index) => onCancelAudienceRule(index, keyName)}
                    onEdit={(index) => onEditAudienceRule(index, keyName)}
                    onRuleUpdate={(index, rule, validateRule) =>
                        onUpdateAudience(index, rule, keyName, validateRule)
                    }
                    filteredQuestionList={filteredQuestionList}
                    currentProject={this.props.currentProject}
                    onSave={(index) => onSaveAudienceRule(index, keyName)}
                    questionList={questionList}
                    audienceRule={addAudienceRule}
                    totalSampleSize={audience.totalSampleSize}
                    onAddDynamicQuestion={onAddDynamicQuestion}
                    validationService={validationService}
                    disableByFieldName={disableByFieldName}
                />
            );
        }
        return null;
    };
    render(): JSX.Element {
        const {
            isAdd,
            isEdited,
            audience,
            previousAudience,
            showError,
            validationService,
            projectHealth,
            isIncaPanelEnabled,
            handleProjectSettings,
            confirmPanelSwitching,
            updatePanelSettings,
            disableByFieldName
        } = this.props;
        return (
            <>
                <div className="audience--panel-screeners-wrapper">
                    <AudienceSetting
                        handleProjectSettings={handleProjectSettings}
                        confirmPanelSwitching={confirmPanelSwitching}
                        currentProject={this.props.currentProject}
                        disableByFieldName={disableByFieldName}
                        audience={audience}
                        isEdited={isEdited}
                        onSaveAudience={this.props.onSaveAudience}
                        isSaving={this.props.isSaving}
                        showError={showError}
                        onCountryChange={this.props.onCountryChange}
                        validationService={validationService}
                        projectHealth={projectHealth}
                    />
                    {/* {this.props.audience.panelType === PanelType.INCA &&
                    invalidFields.length ? (
                        <Notes
                            type={ALERT_MESSAGE_TYPES.DANGER}
                            note={`To use inca Panel, please correct "${invalidFields.join(
                                '/'
                            )}"`}
                        />
                    ) : null} */}
                    <div className="audience--screeners">
                        {isIncaPanelEnabled ? (
                            <div className="audience--screeners--section">
                                <div className="audience--screeners--section--header">
                                    <h3 className="audience--screeners--section--header--title">
                                        Built-in Targeting Options
                                    </h3>
                                    <p>
                                        {`Built-in targeting options will send
                                    information to inca panel to indicate the
                                    types of participants you would like to be
                                    invited to your study. (only available for inca panel)`}
                                    </p>
                                </div>
                                {/* Built in targeting screener section */}
                                {this.renderAudienceRuleList(
                                    AUDIENCE_RULE_TYPES.BUILT_IN_RULE
                                )}
                                {this.renderAddAudienceRule(
                                    AUDIENCE_RULE_TYPES.BUILT_IN_RULE
                                )}
                                <div className="audience--screeners--section--footer">
                                    <button
                                        className="btn btn--md btn--blue"
                                        data-test={
                                            dataTestid.TARGETING_OPTION_BTN
                                        }
                                        onClick={() => {
                                            this.props.openCustomScreenerModal(
                                                AUDIENCE_RULE_TYPES.BUILT_IN_RULE
                                            );
                                        }}
                                        disabled={
                                            this.props.disableByFieldName() ||
                                            isAdd ||
                                            !this.props.isIncaPanelEnabled ||
                                            (this.props.isIncaPanelEnabled &&
                                                this.props.currentProject
                                                    .countries?.length === 0)
                                        }
                                    >
                                        <UilCrosshairs
                                            size="15px"
                                            style={{ marginRight: '5px' }}
                                        />
                                        Add a new targeting option
                                    </button>
                                </div>

                                {/* End of built in targeting screener section */}
                            </div>
                        ) : (
                            <PanelSourceSettings
                                audience={audience}
                                validationService={validationService}
                                updatePanelSettings={updatePanelSettings}
                                currentProject={this.props.currentProject}
                                disableByFieldName={disableByFieldName}
                            />
                        )}
                        <div className="audience--screeners--section">
                            <div className="audience--screeners--section--header">
                                <h3 className="audience--screeners--section--header--title">
                                    Custom Screeners
                                </h3>
                                <p>
                                    {`Custom screeners can be used to terminate participants based off their responses to ensure that only relevant participants are able to complete the full chat.`}
                                </p>
                            </div>
                            {/* Custom screener section */}
                            {this.renderAudienceRuleList()}
                            {this.renderAddAudienceRule()}
                            <div className="audience--screeners--section--footer">
                                <button
                                    className="btn btn--md btn--blue"
                                    data-test={dataTestid.CUSTOM_SCREENER_BTN}
                                    onClick={() => {
                                        this.props.openCustomScreenerModal(
                                            AUDIENCE_RULE_TYPES.CUSTOM_RULE
                                        );
                                    }}
                                >
                                    <UilUser
                                        size="15px"
                                        style={{ marginRight: '5px' }}
                                    />
                                    Add a custom screener
                                </button>
                            </div>

                            {/* End of custom screener section */}
                        </div>
                    </div>
                </div>
                <AudienceSummary
                    audience={previousAudience}
                    currentProject={this.props.currentProject}
                />
            </>
        );
    }
}

export default UserDefinedAudienceGroup;
